import { useMemo } from 'react';

import { useFeatureGate } from '@trello/feature-gate-client';

export interface PlannerFeatureGates {
  isZenModeEnabled: boolean;
  isAutoUpdatingDateEnabled: boolean;
  isSingleCardEventEnabled: boolean;
  isScheduleFromCardQuickActionsEnabled: boolean;
  isUseLoginHintEnabled: boolean;
  isMultiAccountM2Enabled: boolean;
  isCustomOrderEnabled: boolean;
  isInPlaceMessageEventPreviewEnabled: boolean;
  rerenderCalendarToggleFix: boolean;
  clearCacheOnReconnect: boolean;
  isCreateEventMigrationEnabled: boolean;
  isMultiAccountManagementRedesignEnabled: boolean;
}

/**
 * Hook to determine access to various planner features.
 *
 * @returns {PlannerFeatureGates} An object containing boolean flags for different planner features
 */
export function usePlannerFeatureGate(): PlannerFeatureGates {
  const { value: useLoginHint } = useFeatureGate(
    'trello_electric_use_login_hint',
  );
  const { value: autoUpdatingDateGate } = useFeatureGate(
    'xf_planner_auto_updating_date',
  );

  const { value: scheduleFromCardQuickActions } = useFeatureGate(
    'xf_planner_card_quick_actions_scheduler',
  );
  const { value: singleCardEventGate } = useFeatureGate(
    'xf_planner_single_card_event',
  );

  const { value: zenModeGate } = useFeatureGate('phx_zen_mode');
  const { value: isMultiAccountM2Enabled } = useFeatureGate(
    'electric_planner_multi_account_m2',
  );
  const { value: isCustomOrderEnabled } = useFeatureGate(
    'phx_planner_custom_order',
  );
  const { value: isInPlaceMessageEventPreviewEnabled } = useFeatureGate(
    'ghost_is_in_place_message_event_preview_enabled',
  );

  const { value: rerenderCalendarToggleFix } = useFeatureGate(
    'trello_enterprise_planner_calendar_toggle_fix',
  );

  const { value: clearCacheOnReconnect } = useFeatureGate(
    'trello_enterprise_planner_clear_cache_on_reconnect',
  );

  const { value: isCreateEventMigrationEnabled } = useFeatureGate(
    'electric_planner_create_event_migration',
  );
  const { value: isMultiAccountManagementRedesignEnabled } = useFeatureGate(
    'electric_multi_account_management_redesign',
  );

  return useMemo(
    () => ({
      isZenModeEnabled: zenModeGate,
      isSingleCardEventEnabled: singleCardEventGate,
      isAutoUpdatingDateEnabled: autoUpdatingDateGate,
      isScheduleFromCardQuickActionsEnabled: scheduleFromCardQuickActions,
      isUseLoginHintEnabled: useLoginHint,
      isMultiAccountM2Enabled,
      isCustomOrderEnabled,
      isInPlaceMessageEventPreviewEnabled,
      rerenderCalendarToggleFix,
      clearCacheOnReconnect,
      isCreateEventMigrationEnabled,
      isMultiAccountManagementRedesignEnabled,
    }),
    [
      zenModeGate,
      singleCardEventGate,
      autoUpdatingDateGate,
      scheduleFromCardQuickActions,
      useLoginHint,
      isMultiAccountM2Enabled,
      isCustomOrderEnabled,
      isInPlaceMessageEventPreviewEnabled,
      rerenderCalendarToggleFix,
      clearCacheOnReconnect,
      isCreateEventMigrationEnabled,
      isMultiAccountManagementRedesignEnabled,
    ],
  );
}
